import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { StarRating } from "@/components/StarRating";
import { Flame, LocateFixed, ChevronLeft, ChevronRight, MapPin } from "lucide-react";

/**
 * TrendingNearYouCarousel
 *
 * Directory home hero rail. Silently asks for the browser's coarse location on
 * mount; if granted, calls `/businesses?lat=&lng=&radius_km=50` and filters to
 * items with `is_trending === true`. If denied or missing, falls back to
 * global-trending (`?sort=popular`). Renders as a horizontal-scroll rail with
 * left/right nav buttons — matches the pattern the mobile app will use.
 *
 * Hidden entirely (`return null`) when there are zero trending listings, so
 * quiet regions don't show an awkward empty rail.
 */
const CARD_WIDTH = 260;

export default function TrendingNearYouCarousel() {
  const [items, setItems] = useState([]);
  const [scope, setScope] = useState("global"); // 'near_me' | 'global'
  const [radiusMi, setRadiusMi] = useState(null);
  const [loading, setLoading] = useState(true);
  const railRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const fetchTrending = async (params) => {
      const { data } = await api.get("/businesses", { params });
      if (cancelled) return { items: [] };
      const trending = (data.items || []).filter((b) => b.is_trending);
      return { items: trending, radius_km: data.radius_km };
    };

    const geoTry = () => new Promise((resolve) => {
      if (!navigator.geolocation) return resolve(null);
      navigator.geolocation.getCurrentPosition(
        (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        () => resolve(null),
        { enableHighAccuracy: false, maximumAge: 5 * 60_000, timeout: 6000 }
      );
    });

    (async () => {
      const coords = await geoTry();
      const params = coords
        ? { lat: coords.lat, lng: coords.lng, radius_km: 80, sort: "popular", limit: 15 }
        : { sort: "popular", limit: 15 };
      try {
        const res = await fetchTrending(params);
        if (cancelled) return;
        setItems(res.items);
        setScope(coords ? "near_me" : "global");
        if (res.radius_km) setRadiusMi(Math.round(res.radius_km * 0.621371));
      } catch (e) {
        console.warn("trending carousel load failed:", e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const scroll = (dir) => {
    railRef.current?.scrollBy({ left: dir * CARD_WIDTH * 2, behavior: "smooth" });
  };

  const title = useMemo(() => scope === "near_me" ? "Trending near you" : "Trending now", [scope]);

  if (loading) return null;
  if (items.length === 0) return null;

  return (
    <section className="mb-10" data-testid="trending-near-you-carousel">
      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-mono font-semibold uppercase tracking-wider bg-gradient-to-r from-orange-500 to-rose-500 text-white shadow-sm ring-1 ring-orange-500/40">
            <Flame size={11} />
            Trending
          </span>
          <h2 className="font-display text-2xl sm:text-3xl tracking-tighter" data-testid="trending-carousel-title">
            {title}
          </h2>
          {scope === "near_me" && (
            <span className="text-xs font-mono text-gray-500 inline-flex items-center gap-1" data-testid="trending-carousel-scope">
              <LocateFixed size={12} /> within {radiusMi || 50} mi
            </span>
          )}
          <span className="text-xs font-mono text-gray-400">· {items.length} hot listing{items.length === 1 ? "" : "s"}</span>
        </div>
        <div className="flex gap-1">
          <button
            type="button"
            onClick={() => scroll(-1)}
            className="h-8 w-8 rounded-full border border-border bg-white hover:bg-gray-50 shadow-sm inline-flex items-center justify-center text-gray-600 hover:text-[#B31E1E]"
            aria-label="Scroll trending left"
            data-testid="trending-carousel-prev"
          >
            <ChevronLeft size={16} />
          </button>
          <button
            type="button"
            onClick={() => scroll(1)}
            className="h-8 w-8 rounded-full border border-border bg-white hover:bg-gray-50 shadow-sm inline-flex items-center justify-center text-gray-600 hover:text-[#B31E1E]"
            aria-label="Scroll trending right"
            data-testid="trending-carousel-next"
          >
            <ChevronRight size={16} />
          </button>
        </div>
      </div>

      <div
        ref={railRef}
        className="flex gap-3 overflow-x-auto snap-x snap-mandatory pb-2 -mx-1 px-1 scroll-smooth"
        style={{ scrollbarWidth: "thin" }}
        data-testid="trending-carousel-rail"
      >
        {items.map((b) => {
          const hero = b.hero_image || b.logo_data_url || "";
          return (
            <Link
              key={b.id}
              to={`/businesses/${b.id}`}
              className="snap-start shrink-0 w-[240px] sm:w-[260px] rounded-xl border border-border bg-white overflow-hidden hover:shadow-md transition-shadow group"
              data-testid={`trending-carousel-card-${b.id}`}
            >
              <div className="aspect-[4/3] bg-gray-100 relative overflow-hidden">
                {hero ? (
                  <img
                    src={hero}
                    alt={b.name}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center font-display text-4xl text-gray-300">
                    {(b.name || "?")[0]}
                  </div>
                )}
                <span className="absolute top-2 left-2 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-mono font-semibold uppercase tracking-wider bg-gradient-to-r from-orange-500 to-rose-500 text-white shadow ring-1 ring-orange-500/40">
                  <Flame size={10} /> Trending
                </span>
                {typeof b.distance_mi === "number" && (
                  <span className="absolute top-2 right-2 rounded-full px-2 py-0.5 text-[10px] font-mono bg-black/60 text-white">
                    {b.distance_mi < 0.1 ? "< 0.1 mi" : `${b.distance_mi.toFixed(1)} mi`}
                  </span>
                )}
              </div>
              <div className="p-3">
                <h3 className="font-display text-lg tracking-tight leading-tight truncate group-hover:text-[#B31E1E]" title={b.name}>
                  {b.name}
                </h3>
                <div className="mt-1 flex items-center gap-1.5">
                  <StarRating value={b.rating_avg || 0} size={12} />
                  <span className="text-[11px] font-mono tabular-nums text-gray-700">{(b.rating_avg || 0).toFixed(1)}</span>
                  <span className="text-[11px] font-mono text-gray-400">· {b.rating_count || 0}</span>
                </div>
                {(b.city || b.state) && (
                  <p className="mt-1 text-[11px] text-gray-500 inline-flex items-center gap-1 truncate">
                    <MapPin size={10} className="text-gray-400" />
                    <span className="truncate">{[b.city, b.state].filter(Boolean).join(", ")}</span>
                  </p>
                )}
              </div>
            </Link>
          );
        })}
      </div>
    </section>
  );
}
